import { handleError, showAlert, showWait } from '@zyno-io/vue-foundation';

import { useStore } from '@/store';

import { isChunkLoadError, isRepeatChunkLoadFailure, recordChunkLoadFailure } from './chunk-load-error';
import { UpdateChecker } from './update.helpers';

async function runBlockingAsync<T>(fn: () => Promise<T>): Promise<T> {
    const rootStore = useStore();
    rootStore.globalLoader = true;
    try {
        return await fn();
    } finally {
        rootStore.globalLoader = false;
    }
}

export function wrapBlockingAsync<T>(fn: () => Promise<T>): () => Promise<T> {
    return async () => {
        return runBlockingAsync(fn);
    };
}

const CHUNK_RELOAD_KEY = 'zs:chunkReloadAt';
const CHUNK_RELOAD_COOLDOWN_MS = 60_000;

function claimChunkReload() {
    try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
        if (last && Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return false;
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        return true;
    } catch {
        return false; // no storage, no loop guard — don't risk a reload loop
    }
}

export function wrapRuntimeImport<T>(fn: () => Promise<T>): () => Promise<T> {
    return async () => {
        try {
            return await runBlockingAsync(fn);
        } catch (err) {
            if (isChunkLoadError(err)) {
                // Stale tab after a deploy: reload onto the new build, unreported — unless reloading
                // already failed to fix it (see chunk-load-error.ts).
                if (isRepeatChunkLoadFailure()) handleError('Failed to load import', err);
                recordChunkLoadFailure();
                const isUpdateAvailable = UpdateChecker.isUpdateAvailable || (await UpdateChecker.checkNow());
                const canReload = UpdateChecker.canReloadNow();
                if (isUpdateAvailable && !canReload) {
                    // The idle reload takes over once the call ends / the modal closes.
                    await showAlert('ZynoSuite has been updated and will reload once you finish what you are doing.');
                } else if (canReload && (isUpdateAvailable || claimChunkReload())) {
                    // Without a newer version (a rollback, a same-version redeploy) still try one
                    // reload, rate-limited per tab so an asset missing from the live build can't loop.
                    showWait('Updating ZynoSuite...');
                    location.reload();
                } else {
                    await showAlert('ZynoSuite could not load this screen. Check your connection and try again.');
                }
                throw err;
            }

            handleError('Failed to load import', err);
            if (UpdateChecker.isUpdateAvailable) {
                showWait('Updating ZynoSuite...');
            } else {
                await showAlert('An error occurred while loading a ZynoSuite component. The application will now reload and retry.');
                showWait('Reloading ZynoSuite...');
            }
            location.reload();
            throw err;
        }
    };
}
